import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { BrowserModule } from '@angular/platform-browser';
import { COUNTRY_CODE, GDL_SCRIPT_URL, GlobalDataLayerModule, LANGUAGE_CODE } from '@fedex/global-data-layer-client';
import { TranslateLoader, TranslateModule, TranslateStore } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HostContextService } from './services/host-context/host-context.service';
import { BannerMessageComponent } from './features/banner-message/banner-message.component';
import { LocaleInterceptor } from './shared/interceptors/locale/locale.interceptor';
import { MockCaseInterceptor } from './shared/interceptors/mock-case/mock.case.interceptor';
import { MockHostInterceptor } from './shared/interceptors/mock-host/mock.host.interceptor';
import { OauthInterceptor } from './shared/interceptors/oauth/oauth.interceptor';
import { PageHeaderComponent } from './features/page-header/page-header.component';
import { SharedModule } from './shared/shared.module';
import { TwoFactorInterceptor } from './shared/interceptors/two-factor/two-factor.interceptor';
import {
    HEAD_FOOT_CONFIGURATION,
    HeadFootModule,
    LocaleService,
    OptimModule,
    OPTIMIZELY_API_KEY,
    ChevronIcon,
    CircleExclamationIcon,
    CrossIcon,
    IconModule,
    IconRegistry,
    ConfigurationService,
    ConfigurationModule,
    Popr2Module,
    OPTIMIZELY_ATTRIBUTE_SUPPLIERS,
    CommonCoreModule
} from '@ciam/library';
import { SidebarNavModule } from './features/side-bar/sidebar-nav.module';
import { LocaleProvider } from './util/locale-provider';

const tagName = 'user-login-contact-info';

@NgModule({
  declarations: [
    AppComponent,
    PageHeaderComponent,
    BannerMessageComponent
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    Popr2Module,
    CommonCoreModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => new TranslateHttpLoader(http, './assets/i18n/', '.json'),
        deps: [HttpClient]
      }
    }),
    ConfigurationModule.withSource(
      { url: './assets/configs/config.json' },
      { url: './assets/configs/level.json' },
      { url: './assets/configs/oauth.json' },
      { storage: 'local', storageKey: 'wpro_configs', failOnError: false }
    ),
    OptimModule.forRoot({
      apiKeyProvider: {
        provide: OPTIMIZELY_API_KEY,
        useFactory: (config: ConfigurationService): Observable<string> =>
          config.get$<string>('env.host')
            .pipe(take(1), map(host => (host === 'www.fedex.com') ? 'E2PFAGoyMiZkNBzsNCRFs' : '9qnxcngBSCteXbtmAv4AF')),
        deps: [ConfigurationService]
      }
    }),
    HeadFootModule.forRoot({
      configProvider: {
        provide: HEAD_FOOT_CONFIGURATION,
        useFactory: (config: ConfigurationService) => config.dict$({ host: 'env.host', forgeRockLoginEnabled: 'forgeRockLoginEnabled'
        }),
        deps: [ConfigurationService],
      }
    }),
    GlobalDataLayerModule.forRoot({
      appName: 'wpro',
      gdlScriptUrl: {
        provide: GDL_SCRIPT_URL,
        useFactory: (config: ConfigurationService): Observable<string> =>
          config.get$<string>('env.host')
            .pipe(take(1), map(host => `https://${host}/gdl/gdl-fedex.js`)),
        deps: [ConfigurationService],
      },
      loadAppConfig: false,
      countryCode: {
        provide: COUNTRY_CODE,
        useFactory: (localeService: LocaleService) =>
          localeService.solveLocale().country,
        deps: [LocaleService],
      },
      languageCode: {
        provide: LANGUAGE_CODE,
        useFactory: (localeService: LocaleService) =>
          localeService.solveLocale().language,
        deps: [LocaleService],
      },
    }),
    HttpClientModule,
    SharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) =>
          new TranslateHttpLoader(http, './assets/i18n/', '.json'),
        deps: [HttpClient],
      },
    }),
    IconModule,
    HeadFootModule,
    SidebarNavModule,
  ],
  providers: [
    // APP_BASE_HREF - Polaris needs full path, dotcom uses '/' with hash routing
    {
      provide: APP_BASE_HREF,
      useFactory: (hostContext: HostContextService) => {
        const path = window.location.pathname;

        if (hostContext.isPolaris) {
          // Strip the trailing Angular route (and any child segments) to get the
          // MFE mount root. Match the LAST route-name segment, not the first:
          // the mount path itself can contain a segment that collides with a
          // route name (e.g. `/shippingplus/<locale>/profile/info/login` where
          // `info` is part of the mount, not the route). Splitting by segment
          // and cutting at the last route-name keeps the full mount and correctly
          // handles child routes (e.g. `login/2fa`, `info/edit`).
          const routeNames = ['login', 'contact', 'info', 'laboratory'];
          const segments = path.split('/');
          let lastRouteIndex = -1;
          for (let i = 0; i < segments.length; i++) {
            if (routeNames.includes(segments[i])) lastRouteIndex = i;
          }
          const mfeRoot = lastRouteIndex > 0
            ? segments.slice(0, lastRouteIndex).join('/')
            : path.replace(/\/+$/, '');
          const result = mfeRoot.endsWith('/') ? mfeRoot : mfeRoot + '/';
          return result;
        }

        // Dotcom with hash routing - use '/'
        return '/';
      },
      deps: [HostContextService],
    },
    TranslateStore,
    // Block bootstrap until app configuration (config.json / level.json /
    // oauth.json) has finished loading. Interceptors and services (e.g.
    // OauthService, CountryService) read config synchronously via
    // config.select(...), so any API fired before the async load completes
    // builds requests/timeouts from undefined values and fails (e.g.
    // timeout(undefined) throws, timeout(NaN) fires immediately). Gating
    // bootstrap here fixes this for every API/component in both Polaris and
    // dotcom modes. dict$ defers until the ConfigurationService load completes;
    // firstValueFrom returns a Promise so Angular reliably blocks on it.
    provideAppInitializer(() => {
      const config = inject(ConfigurationService);
      return firstValueFrom(config.dict$({ host: 'env.host' }));
    }),
    //[oauth, 2fa, timeout, locale, etc...
    //Interceptors honor order of execution
    { provide: HTTP_INTERCEPTORS, useClass: TwoFactorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LocaleInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MockCaseInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MockHostInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: TimeOutInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: OauthInterceptor, multi: true },
    { provide: OPTIMIZELY_ATTRIBUTE_SUPPLIERS, useClass: LocaleProvider, multi: true }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(
    private iconRegistry: IconRegistry,
    private injector: Injector
  ) {
    this.iconRegistry.register(
      CrossIcon,
      CircleExclamationIcon,
      ChevronIcon
    );

    if (!customElements.get(tagName)) {
      const element = createCustomElement(AppComponent, {
        injector: this.injector,
      });
      customElements.define(tagName, element);
    }
  }
}
